import { skipToken } from '@reduxjs/toolkit/query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Combobox from '@/components/combo-box';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import { useTenant } from '@/hooks';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import {
  useDeleteCourseSectionMutation,
  useDeleteCourseMutation,
  usePatchCourseMutation,
  usePublishCourseMutation,
  usePatchCourseSectionMutation,
  useDeleteCourseTeacherMutation,
  usePatchCourseTeacherMutation,
  useCreateCourseTeachersMutation,
  useCreateCourseSectionMutation,
  useGetBoardsQuery,
  useGetCourseDetailQuery,
  useGetLmsRepoQuery,
  useGetCourseSectionsQuery,
  useGetCourseTeachersQuery,
  useGetUsersQuery,
  usePatchLmsRepoMutation,
  useDeleteLmsRepoMutation,
} from '@/state/course';
import { useGetCurrentUserQuery } from '@/state/user/user-api';
import type { ApiErrorResponse } from '@/types';
import type { CourseSectionRepoItem } from '@/types/api/course.types';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  FileText,
  Info,
  Paperclip,
  PlayCircle,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import ChapterTabsContent from './components/chapter-tabs-content';
import CourseDetailHeader from './components/course-detail-header';
import CourseSectionsDrawer from './components/course-sections-drawer';
import MainSectionsContent from './components/main-sections-content';
import { LeaveRequestDialog } from './components/leave-request-dialog';
import CourseFeedbackFloating from './components/course-feedback-floating';
import {
  AddInstructorsDialog,
  RemoveInstructorDialog,
} from './components/instructors-tab';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AvatarEditor } from '@/components/avatar-editor';
import CourseGradingSchemeDialog from './components/course-grading-scheme-dialog';

const CourseDetailPage = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { isStudent: isLearner, isSchoolAdmin } = useClientPermissions();
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const [chapterName, setChapterName] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [isAddInstructorOpen, setIsAddInstructorOpen] = useState(false);
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<string[]>([]);
  const [selectedInstructorOption, setSelectedInstructorOption] = useState('');
  const [teacherMappingToDelete, setTeacherMappingToDelete] = useState<number | null>(
    null
  );
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoTarget, setVideoTarget] = useState<'chapter' | 'course'>('chapter');
  const [editingVideo, setEditingVideo] = useState<CourseSectionRepoItem | null>(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);
  const [videoToDelete, setVideoToDelete] = useState<CourseSectionRepoItem | null>(
    null
  );
  const [documentToDelete, setDocumentToDelete] =
    useState<CourseSectionRepoItem | null>(null);
  const [isEditCourseOpen, setIsEditCourseOpen] = useState(false);
  const [isDeleteCourseOpen, setIsDeleteCourseOpen] = useState(false);
  const [editCourseName, setEditCourseName] = useState('');
  const [editCourseCode, setEditCourseCode] = useState('');
  const [editCourseFullMarks, setEditCourseFullMarks] = useState('');
  const [editBoardId, setEditBoardId] = useState('');
  const [editLecturerId, setEditLecturerId] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const courseFileInputRef = useRef<HTMLInputElement | null>(null);
  const thumbnailFileInputRef = useRef<HTMLInputElement | null>(null);
  const [thumbnailEditorOpen, setThumbnailEditorOpen] = useState(false);
  const [thumbnailSourceImage, setThumbnailSourceImage] = useState('');
  const [isGradingSchemeDialogOpen, setIsGradingSchemeDialogOpen] = useState(false);
  const parsedCourseId = Number(courseId);
  const mainSectionParam = searchParams.get('section');
  const parsedChapterId = Number(searchParams.get('chapter'));
  const parsedChapterTab = Number(searchParams.get('tab'));
  const selectedSectionId =
    Number.isFinite(parsedChapterId) && parsedChapterId > 0
      ? parsedChapterId
      : null;
  const chapterTabMap = {
    1: 'chapter-details',
    2: 'videos',
    3: 'attachments',
    4: 'assignments',
  } as const;
  const chapterTabToNumber = {
    'chapter-details': 1,
    videos: 2,
    attachments: 3,
    assignments: 4,
  } as const;
  const validCourseId = Number.isFinite(parsedCourseId) && parsedCourseId > 0;
  const queryArg =
    tenant && validCourseId
      ? { tenant, courseId: parsedCourseId }
      : skipToken;
  const [createCourseSection, { isLoading: isCreatingSection }] =
    useCreateCourseSectionMutation();
  const [patchCourse, { isLoading: isPatchingCourse }] = usePatchCourseMutation();
  const [publishCourse, { isLoading: isPublishingCourse }] =
    usePublishCourseMutation();
  const [deleteCourse, { isLoading: isDeletingCourse }] =
    useDeleteCourseMutation();
  const [deleteCourseSection, { isLoading: isDeletingSection }] =
    useDeleteCourseSectionMutation();
  const [patchCourseSection, { isLoading: isPatchingSection }] =
    usePatchCourseSectionMutation();
  const [createCourseTeachers, { isLoading: isCreatingTeachers }] =
    useCreateCourseTeachersMutation();
  const [patchCourseTeacher, { isLoading: isPatchingTeacher }] =
    usePatchCourseTeacherMutation();
  const [deleteCourseTeacher, { isLoading: isDeletingTeacher }] =
    useDeleteCourseTeacherMutation();
  const [patchLmsRepo, { isLoading: isPatchingLmsRepo }] = usePatchLmsRepoMutation();
  const [deleteLmsRepo, { isLoading: isDeletingLmsRepo }] = useDeleteLmsRepoMutation();

  const {
    data: course,
    isLoading: isCourseLoading,
    error: courseError,
    refetch: refetchCourse,
  } = useGetCourseDetailQuery(queryArg);

  const {
    data: currentUser,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserQuery(tenant ? { tenant } : skipToken);

  const {
    data: courseTeachers,
    isLoading: isTeachersLoading,
    error: teachersError,
    refetch: refetchTeachers,
  } = useGetCourseTeachersQuery(queryArg);
  const { data: users = [] } = useGetUsersQuery(tenant ? { tenant } : skipToken);
  const { data: boards = [] } = useGetBoardsQuery(tenant ? { tenant } : skipToken);

  const {
    data: courseSections,
    isLoading: isSectionsLoading,
    error: sectionsError,
    refetch: refetchSections,
  } = useGetCourseSectionsQuery(queryArg);

  const canEdit = useMemo(() => {
    if (!course || !currentUser) return false;
    if (course.lecturer?.id === currentUser.id) return true;
    return (
      courseTeachers?.some((teacherMapping) => {
        return teacherMapping.teacher?.id === currentUser.id;
      }) ?? false
    );
  }, [course, currentUser, courseTeachers]);

  const isLoading =
    isCourseLoading || isUserLoading || isTeachersLoading || isSectionsLoading;
  const error = (courseError ||
    userError ||
    teachersError ||
    sectionsError) as ApiErrorResponse;
  const activeSectionId = selectedSectionId ?? courseSections?.[0]?.id ?? null;
  const {
    data: lmsRepoItems = [],
    isLoading: isRepoLoading,
    refetch: refetchLmsRepo,
  } = useGetLmsRepoQuery(
    tenant && validCourseId && activeSectionId
      ? { tenant, courseId: parsedCourseId, courseSectionId: activeSectionId }
      : skipToken
  );
  const selectedSection = useMemo(
    () => courseSections?.find((section) => section.id === activeSectionId) ?? null,
    [courseSections, activeSectionId]
  );
  const isChapterView = selectedSectionId !== null && !!selectedSection;
  const navigableSections = useMemo(
    () => (courseSections || []).filter((section) => canEdit || section.is_active),
    [canEdit, courseSections]
  );
  const currentSectionIndex = useMemo(
    () => navigableSections.findIndex((section) => section.id === activeSectionId),
    [activeSectionId, navigableSections]
  );
  const previousSection = currentSectionIndex > 0 ? navigableSections[currentSectionIndex - 1] : null;
  const nextSection =
    currentSectionIndex >= 0 && currentSectionIndex < navigableSections.length - 1
      ? navigableSections[currentSectionIndex + 1]
      : null;
  const sectionVideos = useMemo(
    () =>
      (selectedSection?.course_section_repo || []).filter(
        (item) => Boolean(item.content && item.content.trim()) && !item.document
      ),
    [selectedSection?.course_section_repo]
  );
  const sectionDocuments = useMemo(
    () => lmsRepoItems.filter((item) => Boolean(item.document)),
    [lmsRepoItems]
  );
  const courseRepoVideos = useMemo(
    () =>
      (course?.course_repo || []).filter(
        (item) => Boolean(item.content && item.content.trim()) && !item.document
      ),
    [course?.course_repo]
  );
  const courseMainVideos = useMemo(
    () => courseRepoVideos.filter((item) => item.course_section === null),
    [courseRepoVideos]
  );
  const courseRepoDocuments = useMemo(
    () => (course?.course_repo || []).filter((item) => Boolean(item.document)),
    [course?.course_repo]
  );
  const courseMainDocuments = useMemo(
    () => courseRepoDocuments.filter((item) => item.course_section === null),
    [courseRepoDocuments]
  );
  const sectionVideoGroups = useMemo(() => {
    const groups = new Map<number, { sectionId: number; sectionName: string; count: number }>();
    courseRepoVideos.forEach((item) => {
      if (!item.course_section) return;
      const sectionId = item.course_section;
      const sectionMeta = courseSections?.find((section) => section.id === sectionId);
      if (!sectionMeta?.is_active) return;
      const existing = groups.get(sectionId);
      if (existing) {
        existing.count += 1;
        return;
      }
      const sectionName = sectionMeta.section_name || `Section ${sectionId}`;
      groups.set(sectionId, { sectionId, sectionName, count: 1 });
    });
    return Array.from(groups.values());
  }, [courseRepoVideos, courseSections]);
  const sectionAttachmentGroups = useMemo(() => {
    const groups = new Map<number, { sectionId: number; sectionName: string; count: number }>();
    courseRepoDocuments.forEach((item) => {
      if (!item.course_section) return;
      const sectionId = item.course_section;
      const sectionMeta = courseSections?.find((section) => section.id === sectionId);
      if (!sectionMeta?.is_active) return;
      const existing = groups.get(sectionId);
      if (existing) {
        existing.count += 1;
        return;
      }
      const sectionName = sectionMeta.section_name || `Section ${sectionId}`;
      groups.set(sectionId, { sectionId, sectionName, count: 1 });
    });
    return Array.from(groups.values());
  }, [courseRepoDocuments, courseSections]);

  const handleAddChapter = async () => {
    const sectionName = chapterName.trim();
    if (!tenant || !validCourseId || !sectionName) return;

    try {
      await createCourseSection({
        tenant,
        data: {
          section_name: sectionName,
          course: String(parsedCourseId),
        },
      }).unwrap();
      setChapterName('');
      await refetchSections();
      toast.success('Chapter added successfully');
    } catch {
      toast.error('Failed to add chapter');
    }
  };

  const handleDeleteChapter = async (sectionId: number) => {
    if (!tenant) return;

    try {
      await deleteCourseSection({ tenant, sectionId }).unwrap();
      if (selectedSectionId === sectionId) {
        navigate(`/${tenant}/courses/${parsedCourseId}`);
      }
      await refetchSections();
      toast.success('Chapter deleted successfully');
    } catch {
      toast.error('Failed to delete chapter');
    }
  };

  const handleToggleChapterLock = async (sectionId: number, isActive: boolean) => {
    if (!tenant) return;

    const nextIsActive = !isActive;

    try {
      await patchCourseSection({
        tenant,
        sectionId,
        data: { is_active: nextIsActive },
      }).unwrap();
      await refetchSections();
      toast.success(
        nextIsActive
          ? 'Chapter unlocked successfully'
          : 'Chapter locked successfully'
      );
    } catch {
      toast.error('Failed to update chapter status');
    }
  };

  const isInstructorInCourse = canEdit;
  const isEnrolledInCourse = isLearner || isInstructorInCourse;
  const showNotEnrolledNote = isSchoolAdmin && !isEnrolledInCourse;
  const availableMainSections = useMemo(() => {
    if (showNotEnrolledNote) return [] as string[];
    if (isInstructorInCourse) {
      return [
        'course-details',
        'videos',
        'attachments',
        'assignments',
        'users',
        'attendance',
      ];
    }
    if (isLearner) {
      return ['course-details', 'videos', 'attachments', 'assignments', 'analytics'];
    }
    return [];
  }, [showNotEnrolledNote, isInstructorInCourse, isLearner]);
  const currentMainSection = useMemo(() => {
    if (showNotEnrolledNote) return 'course-details';
    if (!availableMainSections.length) return '';
    if (mainSectionParam && availableMainSections.includes(mainSectionParam)) {
      return mainSectionParam;
    }
    return 'course-details';
  }, [availableMainSections, mainSectionParam, showNotEnrolledNote]);
  const currentChapterTab = useMemo(() => {
    if (!Number.isFinite(parsedChapterTab)) return 'chapter-details';
    return chapterTabMap[parsedChapterTab as keyof typeof chapterTabMap] || 'chapter-details';
  }, [parsedChapterTab]);
  const showChapterDrawer =
    !showNotEnrolledNote && (currentMainSection === 'course-details' || isChapterView);
  const workspaceModes = useMemo(() => {
    if (showNotEnrolledNote) return [];

    if (isInstructorInCourse) {
      return [
        { id: 'course-details', icon: FileText, label: 'Course details' },
        { id: 'videos', icon: PlayCircle, label: 'Videos' },
        { id: 'attachments', icon: Paperclip, label: 'Attachments' },
        { id: 'assignments', icon: ClipboardList, label: 'Assignments' },
        { id: 'users', icon: Users, label: 'Users' },
        { id: 'attendance', icon: CalendarDays, label: 'Attendance' },
      ] as const;
    }

    if (isLearner) {
      return [
        { id: 'course-details', icon: FileText, label: 'Course details' },
        { id: 'videos', icon: PlayCircle, label: 'Videos' },
        { id: 'attachments', icon: Paperclip, label: 'Attachments' },
        { id: 'assignments', icon: ClipboardList, label: 'Assignments' },
        { id: 'analytics', icon: BarChart3, label: 'Analytics' },
      ] as const;
    }

    return [];
  }, [isInstructorInCourse, isLearner, showNotEnrolledNote]);
  useEffect(() => {
    if (!tenant || !validCourseId || !availableMainSections.length) return;

    const params = new URLSearchParams(searchParams);
    let changed = false;

    if (!params.get('section')) {
      params.set('section', 'course-details');
      changed = true;
    }

    if (selectedSectionId && !params.get('tab')) {
      params.set('tab', '1');
      changed = true;
    }

    if (changed) {
      navigate(`/${tenant}/courses/${parsedCourseId}?${params.toString()}`, {
        replace: true,
      });
    }
  }, [
    availableMainSections.length,
    navigate,
    parsedCourseId,
    searchParams,
    selectedSectionId,
    tenant,
    validCourseId,
  ]);

  const navigateToCourse = () => {
    if (!tenant || !validCourseId) return;
    const params = new URLSearchParams(searchParams);
    params.set('section', 'course-details');
    params.delete('chapter');
    params.delete('tab');
    navigate(`/${tenant}/courses/${parsedCourseId}?${params.toString()}`);
  };

  const navigateToSection = (sectionId: number, chapterTabNumber = 1) => {
    if (!tenant || !validCourseId) return;
    const params = new URLSearchParams(searchParams);
    params.set('chapter', String(sectionId));
    params.set('tab', String(chapterTabNumber));
    if (!params.get('section')) {
      params.set('section', 'course-details');
    }
    navigate(`/${tenant}/courses/${parsedCourseId}?${params.toString()}`);
  };
  const handleMainSectionChange = (section: string) => {
    if (!tenant || !validCourseId) return;
    const params = new URLSearchParams(searchParams);
    params.set('section', section);
    params.delete('chapter');
    params.delete('tab');
    navigate(`/${tenant}/courses/${parsedCourseId}?${params.toString()}`);
  };

  const existingInstructorIds = useMemo(() => {
    const ids = new Set<string>();
    if (course?.lecturer?.id) ids.add(course.lecturer.id);
    (courseTeachers || []).forEach((mapping) => {
      if (mapping.course === parsedCourseId && mapping.teacher?.id) {
        ids.add(mapping.teacher.id);
      }
    });
    return ids;
  }, [course, courseTeachers, parsedCourseId]);

  const availableInstructorOptions = useMemo(() => {
    return users
      .filter(
        (user) =>
          !existingInstructorIds.has(user.id) &&
          !selectedInstructorIds.includes(user.id)
      )
      .map((user) => ({
        value: user.id,
        label: `${user.profile.first_name} ${user.profile.last_name} (${user.email})`,
      }));
  }, [users, existingInstructorIds, selectedInstructorIds]);

  const selectedInstructorUsers = useMemo(() => {
    const selectedIds = new Set(selectedInstructorIds);
    return users.filter((user) => selectedIds.has(user.id));
  }, [users, selectedInstructorIds]);

  const teacherMappingToDeleteData = useMemo(
    () => courseTeachers?.find((m) => m.id === teacherMappingToDelete) ?? null,
    [courseTeachers, teacherMappingToDelete]
  );
  const boardOptions = useMemo(
    () =>
      boards.map((board) => ({
        label: board.board_name,
        value: String(board.id),
      })),
    [boards]
  );
  const lecturerOptions = useMemo(
    () =>
      users.map((user) => ({
        label: `${user.profile.first_name} ${user.profile.last_name}`,
        value: user.id,
      })),
    [users]
  );

  const handleAddInstructorSelection = (userId: string) => {
    if (!userId) return;
    setSelectedInstructorIds((prev) =>
      prev.includes(userId) ? prev : [...prev, userId]
    );
    setSelectedInstructorOption('');
  };

  const handleRemoveSelectedInstructor = (userId: string) => {
    setSelectedInstructorIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleSubmitInstructors = async () => {
    if (!tenant || !validCourseId || selectedInstructorIds.length === 0) return;

    try {
      await createCourseTeachers({
        tenant,
        data: {
          teachers: selectedInstructorIds.map((teacherId) => ({
            teacher: teacherId,
            course: parsedCourseId,
          })),
        },
      }).unwrap();
      setSelectedInstructorIds([]);
      setSelectedInstructorOption('');
      setIsAddInstructorOpen(false);
      await refetchTeachers();
      toast.success('Instructors added successfully');
    } catch {
      toast.error('Failed to add instructors');
    }
  };

  const handleToggleInstructorActive = async (
    teacherMappingId: number,
    active: boolean
  ) => {
    if (!tenant || !validCourseId) return;
    const nextActive = !active;

    try {
      await patchCourseTeacher({
        tenant,
        teacherMappingId,
        data: {
          active: nextActive,
          semester_course: parsedCourseId,
        },
      }).unwrap();
      await refetchTeachers();
      toast.success(
        nextActive
          ? 'Instructor marked as active'
          : 'Instructor marked as inactive'
      );
    } catch {
      toast.error('Failed to update instructor status');
    }
  };

  const handleDeleteInstructor = async () => {
    if (!tenant || !teacherMappingToDelete) return;

    try {
      await deleteCourseTeacher({
        tenant,
        teacherMappingId: teacherMappingToDelete,
      }).unwrap();
      setTeacherMappingToDelete(null);
      await refetchTeachers();
      toast.success('Instructor removed successfully');
    } catch {
      toast.error('Failed to remove instructor');
    }
  };

  const openEditCourse = () => {
    if (!course) return;
    setEditCourseName(course.course_name || '');
    setEditCourseCode(course.course_code || '');
    setEditCourseFullMarks(course.full_marks != null ? String(course.full_marks) : '');
    setEditBoardId(course.board != null ? String(course.board) : '');
    setEditLecturerId(course.lecturer?.id || '');
    setIsEditCourseOpen(true);
  };

  const getTodayDateISO = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;
  };

  const handlePublishCourse = async () => {
    if (!tenant || !validCourseId) return;
    try {
      await publishCourse({
        tenant,
        courseId: parsedCourseId,
        data: { id: String(parsedCourseId), active: true },
      }).unwrap();
      await refetchCourse();
      toast.success('Course published successfully');
    } catch {
      toast.error('Failed to publish course');
    }
  };

  const handleEditCourseSubmit = async () => {
    if (!tenant || !validCourseId) return;
    if (
      !editCourseName.trim() ||
      !editCourseCode.trim() ||
      !editCourseFullMarks.trim() ||
      !editBoardId ||
      !editLecturerId
    ) {
      toast.error('Please fill all required fields');
      return;
    }
    const termDate = getTodayDateISO();
    const formData = new FormData();
    formData.append('course_name', editCourseName.trim());
    formData.append('course_code', editCourseCode.trim());
    formData.append('term_start_date', termDate);
    formData.append('term_end_date', termDate);
    formData.append('full_marks', editCourseFullMarks.trim());
    formData.append('board', editBoardId);
    formData.append('lecturer', editLecturerId);
    try {
      await patchCourse({
        tenant,
        courseId: parsedCourseId,
        data: formData,
      }).unwrap();
      await refetchCourse();
      setIsEditCourseOpen(false);
      toast.success('Course updated successfully');
    } catch {
      toast.error('Failed to update course');
    }
  };

  const handleDeleteCourse = async () => {
    if (!tenant || !validCourseId) return;
    try {
      await deleteCourse({ tenant, courseId: parsedCourseId }).unwrap();
      toast.success('Course deleted successfully');
      navigate(`/${tenant}/courses`);
    } catch {
      toast.error('Failed to delete course');
    }
  };

  const openAddVideoModal = (target: 'chapter' | 'course') => {
    setEditingVideo(null);
    setVideoTarget(target);
    setVideoTitle('');
    setVideoLink('');
    setIsVideoModalOpen(true);
  };

  const openEditVideoModal = (video: CourseSectionRepoItem) => {
    setEditingVideo(video);
    setVideoTarget(video.course_section ? 'chapter' : 'course');
    setVideoTitle(video.name || '');
    setVideoLink(video.content || '');
    setIsVideoModalOpen(true);
  };

  const handleSubmitVideo = async () => {
    if (!tenant || !videoTitle.trim() || !videoLink.trim()) return;
    const targetSectionId = selectedSection?.id ?? null;
    if (videoTarget === 'chapter' && !targetSectionId) return;

    try {
      if (editingVideo) {
        await patchLmsRepo({
          tenant,
          repoId: editingVideo.id,
          data: {
            content: videoLink.trim(),
            name: videoTitle.trim(),
            description: '-',
          },
        }).unwrap();
        toast.success('Video link updated successfully');
      } else {
        if (videoTarget === 'chapter') {
          const chapterSectionId = targetSectionId;
          if (!chapterSectionId) return;
          const formData = new FormData();
          formData.append(
            'course_section_repo',
            JSON.stringify([
              {
                content: videoLink.trim(),
                name: videoTitle.trim(),
                description: '-',
              },
            ])
          );

          await patchCourseSection({
            tenant,
            sectionId: chapterSectionId,
            data: formData,
          }).unwrap();
        } else if (validCourseId) {
          await patchCourse({
            tenant,
            courseId: parsedCourseId,
            data: {
              course_repo: [
                {
                  content: videoLink.trim(),
                  name: videoTitle.trim(),
                  description: '-',
                },
              ],
            },
          }).unwrap();
        }
        toast.success('Video link added successfully');
      }

      await refetchSections();
      await refetchCourse();
      setIsVideoModalOpen(false);
      setEditingVideo(null);
      setVideoTarget('chapter');
      setVideoTitle('');
      setVideoLink('');
    } catch {
      toast.error(editingVideo ? 'Failed to update video link' : 'Failed to add video link');
    }
  };

  const handleDeleteVideo = async () => {
    if (!tenant || !videoToDelete) return;
    try {
      await deleteLmsRepo({ tenant, repoId: videoToDelete.id }).unwrap();
      await refetchSections();
      await refetchCourse();
      setVideoToDelete(null);
      toast.success('Video link deleted successfully');
    } catch {
      toast.error('Failed to delete video link');
    }
  };

  const handleUploadDocument = async (file: File, target: 'chapter' | 'course') => {
    if (!tenant) return;
    if (target === 'chapter' && !activeSectionId) return;
    if (target === 'course' && !validCourseId) return;

    try {
      const formData = new FormData();
      formData.append('repo_files', file);

      if (target === 'chapter') {
        await patchCourseSection({
          tenant,
          sectionId: activeSectionId as number,
          data: formData,
        }).unwrap();
      } else {
        await patchCourse({
          tenant,
          courseId: parsedCourseId,
          data: formData,
        }).unwrap();
      }

      await refetchLmsRepo();
      await refetchCourse();
      toast.success('Document uploaded successfully');
    } catch {
      toast.error('Failed to upload document');
    }
  };

  const handleDeleteDocument = async () => {
    if (!tenant || !documentToDelete) return;

    try {
      await deleteLmsRepo({ tenant, repoId: documentToDelete.id }).unwrap();
      await refetchLmsRepo();
      setDocumentToDelete(null);
      toast.success('Document deleted successfully');
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const openThumbnailPicker = () => {
    thumbnailFileInputRef.current?.click();
  };

  const handleThumbnailFilePicked = (file?: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result) return;
      setThumbnailSourceImage(result);
      setThumbnailEditorOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveThumbnail = async (_croppedImage: string, file: File) => {
    if (!tenant || !validCourseId) return;
    try {
      const formData = new FormData();
      formData.append('course_banner', file);
      await patchCourse({
        tenant,
        courseId: parsedCourseId,
        data: formData,
      }).unwrap();
      toast.success('Course thumbnail updated successfully.');
      setThumbnailEditorOpen(false);
      setThumbnailSourceImage('');
    } catch {
      toast.error('Failed to update course thumbnail.');
    }
  };

  const handleSelectGradingScheme = async (gradingStructureId: number | null) => {
    if (!tenant || !validCourseId) return;
    try {
      await patchCourse({
        tenant,
        courseId: parsedCourseId,
        data: {
          grading_structure: gradingStructureId,
        },
      }).unwrap();
      toast.success(
        gradingStructureId ? 'Grading scheme updated successfully.' : 'Grading scheme removed.'
      );
      await refetchCourse();
    } catch {
      toast.error('Failed to update grading scheme.');
    }
  };

  return (
    <WithLoadingErrorWrapper
      loading={isLoading}
      error={error}
      onRetry={async () => {
        void refetchTeachers();
        void refetchSections();
        return refetchCourse();
      }}
    >
      <div className="space-y-5 max-w-screen-2xl mx-auto">
        {!isChapterView && showNotEnrolledNote && (
          <div className="rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            You are not enrolled in the course.
          </div>
        )}
        <CourseDetailHeader
          course={course}
          workspaceModes={workspaceModes}
          activeMode={currentMainSection}
          onModeChange={handleMainSectionChange}
          hideTopControls={showNotEnrolledNote}
          canEdit={canEdit}
          onPublish={() => {
            void handlePublishCourse();
          }}
          isPublishing={isPublishingCourse}
          onEdit={openEditCourse}
          onUpdateThumbnail={openThumbnailPicker}
          onManageGradingScheme={() => setIsGradingSchemeDialogOpen(true)}
          gradingSchemeActionLabel={
            course?.grading_structure ? 'Update course grading scheme' : 'Add course grading scheme'
          }
          onDelete={() => setIsDeleteCourseOpen(true)}
          isDeleting={isDeletingCourse}
          onBack={() => {
            if (!tenant) return;
            navigate(`/${tenant}/courses`);
          }}
          rightAction={
            isLearner && tenant && validCourseId && currentUser?.id ? (
              <LeaveRequestDialog
                tenant={tenant}
                courseId={parsedCourseId}
                studentId={currentUser.id}
              />
            ) : null
          }
        />

        <div className="relative">
          {showChapterDrawer && (
            <div className="flex justify-end lg:hidden mb-2">
            <Button
              type="button"
              size="sm"
              variant="default"
              className="h-8 rounded-md shadow-md border border-primary/30"
              onClick={() => setIsDrawerOpen((prev) => !prev)}
            >
              {isDrawerOpen ? 'Hide chapters' : 'Show chapters'}
            </Button>
            </div>
          )}
          {showChapterDrawer && (
            <Button
            type="button"
            size="icon"
            variant="default"
            className="hidden lg:inline-flex absolute top-2 right-2 z-40 h-8 w-8 rounded-md shadow-md border border-primary/30"
            onClick={() => setIsDrawerOpen((prev) => !prev)}
          >
            {isDrawerOpen ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <ChevronsLeft className="h-4 w-4" />
            )}
            </Button>
          )}

          <div
            className={`grid items-start gap-0 transition-all ${showChapterDrawer && isDrawerOpen
              ? 'grid-cols-1 gap-4 lg:gap-0 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_370px]'
              : 'grid-cols-1 gap-4'
              }`}
          >
            <div className="space-y-4 min-w-0 lg:pr-4">
              {isChapterView ? (
                <div className="space-y-3">
                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="break-words text-2xl font-semibold tracking-tight sm:text-3xl">
                      {selectedSection?.section_name}
                    </h2>
                    <Button variant="outline" className="w-full sm:w-auto" onClick={navigateToCourse}>
                      Back to course
                    </Button>
                  </div>
                  <ChapterTabsContent
                    selectedSection={selectedSection}
                    courseStructureId={course?.course_structure ?? 0}
                    currentChapterTab={currentChapterTab}
                    onChapterTabChange={(value) => {
                      if (!tenant || !validCourseId) return;
                      const tabNumber =
                        chapterTabToNumber[value as keyof typeof chapterTabToNumber] || 1;
                      const params = new URLSearchParams(searchParams);
                      params.set('tab', String(tabNumber));
                      navigate(`/${tenant}/courses/${parsedCourseId}?${params.toString()}`);
                    }}
                    sectionVideos={sectionVideos}
                    sectionDocuments={sectionDocuments}
                    canEdit={canEdit}
                    onOpenAddVideoModal={() => openAddVideoModal('chapter')}
                    onOpenEditVideoModal={openEditVideoModal}
                    onSetVideoToDelete={setVideoToDelete}
                    isPatchingLmsRepo={isPatchingLmsRepo}
                    isDeletingLmsRepo={isDeletingLmsRepo}
                    playingVideoId={playingVideoId}
                    onSetPlayingVideoId={setPlayingVideoId}
                    fileInputRef={fileInputRef}
                    onUploadChapterFile={(file) => {
                      void handleUploadDocument(file, 'chapter');
                    }}
                    onSetDocumentToDelete={setDocumentToDelete}
                    isRepoLoading={isRepoLoading}
                  />
                  <div className="pointer-events-none sticky bottom-4 z-30 flex justify-center px-1">
                    <div className="pointer-events-auto relative overflow-hidden rounded-full border border-white/20 bg-white/6 p-[0.35rem] shadow-[0_14px_36px_rgba(15,23,42,0.08)] ring-1 ring-white/10 backdrop-blur-3xl supports-[backdrop-filter]:bg-white/4 dark:border-white/8 dark:bg-white/4 dark:ring-white/6">
                      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.22),rgba(255,255,255,0.06)_42%,rgba(255,255,255,0.015))] dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.035)_42%,rgba(255,255,255,0.01))]" />
                      <div className="pointer-events-none absolute inset-x-8 top-1 h-1/2 rounded-full bg-white/10 blur-xl dark:bg-white/5" />
                      <div className="relative flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full border-white/14 bg-white/14 text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.28)] backdrop-blur-md hover:bg-white/20 hover:text-slate-950 dark:border-white/8 dark:bg-white/8 dark:text-white dark:hover:bg-white/12"
                        onClick={() => previousSection && navigateToSection(previousSection.id, parsedChapterTab || 1)}
                        disabled={!previousSection}
                      >
                        <ChevronLeft className="h-4 w-4 shrink-0 sm:mr-2" />
                        <span className="hidden sm:inline">Previous</span>
                      </Button>
                      <Button
                        type="button"
                        className="rounded-full border-white/14 bg-white/16 text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.3)] backdrop-blur-md hover:bg-white/22 hover:text-slate-950 dark:border-white/8 dark:bg-white/9 dark:text-white dark:hover:bg-white/14"
                        onClick={() => nextSection && navigateToSection(nextSection.id, parsedChapterTab || 1)}
                        disabled={!nextSection}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="h-4 w-4 shrink-0 sm:ml-2" />
                      </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <MainSectionsContent
                  tenant={tenant || ''}
                  courseId={parsedCourseId}
                  currentMainSection={currentMainSection}
                  course={course}
                  boardId={course?.board ?? null}
                  boardName={course?.board_display?.board_name ?? null}
                  canEdit={canEdit}
                  isLearner={isLearner}
                  currentUserId={currentUser?.id ?? null}
                  courseTeachers={courseTeachers}
                  onOpenAddInstructor={() => setIsAddInstructorOpen(true)}
                  onToggleInstructorActive={(teacherMappingId, active) => {
                    void handleToggleInstructorActive(teacherMappingId, active);
                  }}
                  isPatchingTeacher={isPatchingTeacher}
                  onOpenDeleteInstructor={(teacherMappingId) =>
                    setTeacherMappingToDelete(teacherMappingId)
                  }
                  isDeletingTeacher={isDeletingTeacher}
                  courseMainVideos={courseMainVideos}
                  sectionVideoGroups={sectionVideoGroups}
                  onNavigateToSectionVideos={(sectionId) => navigateToSection(sectionId, 2)}
                  onOpenAddCourseVideoModal={() => openAddVideoModal('course')}
                  onOpenEditVideoModal={openEditVideoModal}
                  onSetVideoToDelete={setVideoToDelete}
                  isPatchingLmsRepo={isPatchingLmsRepo}
                  isDeletingLmsRepo={isDeletingLmsRepo}
                  playingVideoId={playingVideoId}
                  onSetPlayingVideoId={setPlayingVideoId}
                  courseMainDocuments={courseMainDocuments}
                  sectionAttachmentGroups={sectionAttachmentGroups}
                  onNavigateToSectionAttachments={(sectionId) => navigateToSection(sectionId, 3)}
                  onNavigateToSectionAssignments={(sectionId) => navigateToSection(sectionId, 4)}
                  courseFileInputRef={courseFileInputRef}
                  onUploadCourseFile={(file) => {
                    void handleUploadDocument(file, 'course');
                  }}
                  onSetDocumentToDelete={setDocumentToDelete}
                />
              )}
            </div>

            {showChapterDrawer && (
              <CourseSectionsDrawer
                isDrawerOpen={isDrawerOpen}
                isLearner={isLearner}
                canEdit={canEdit}
                onCourseDetailsClick={navigateToCourse}
                chapterName={chapterName}
                setChapterName={setChapterName}
                onAddChapter={() => {
                  void handleAddChapter();
                }}
                isCreatingSection={isCreatingSection}
                courseSections={courseSections}
                activeSectionId={activeSectionId}
                onSelectSection={navigateToSection}
                onDeleteSection={(sectionId) => {
                  void handleDeleteChapter(sectionId);
                }}
                isDeletingSection={isDeletingSection}
                onToggleSectionLock={(sectionId, isActive) => {
                  void handleToggleChapterLock(sectionId, isActive);
                }}
                isPatchingSection={isPatchingSection}
              />
            )}
          </div>
        </div>
      </div>

      {tenant && validCourseId && isLearner ? (
        <CourseFeedbackFloating tenant={tenant} courseId={parsedCourseId} />
      ) : null}

      <input
        ref={thumbnailFileInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        onChange={(event) => {
          const file = event.target.files?.[0] || null;
          handleThumbnailFilePicked(file);
          event.target.value = '';
        }}
      />

      <AvatarEditor
        open={thumbnailEditorOpen}
        onClose={() => {
          setThumbnailEditorOpen(false);
          setThumbnailSourceImage('');
        }}
        onSave={handleSaveThumbnail}
        image={thumbnailSourceImage}
        borderRadius={0}
        aspectRatio={1.5}
      />

      <CourseGradingSchemeDialog
        tenant={tenant}
        open={isGradingSchemeDialogOpen}
        selectedGradingStructure={course?.grading_structure}
        isSelectingScheme={isPatchingCourse}
        onOpenChange={setIsGradingSchemeDialogOpen}
        onSelectScheme={(gradingStructureId) => {
          void handleSelectGradingScheme(gradingStructureId);
        }}
      />

      <Dialog
        open={isEditCourseOpen}
        onOpenChange={(open) => {
          setIsEditCourseOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[625px]">
          <DialogHeader className="mb-2">
            <DialogTitle>Edit Course</DialogTitle>
            <DialogDescription>Update course details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-sm font-medium">Course Name</p>
              <Input
                value={editCourseName}
                onChange={(e) => setEditCourseName(e.target.value)}
                placeholder="Enter course name"
              />
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium">Board</p>
              <Combobox
                options={boardOptions}
                selectedOption={editBoardId}
                onSelect={setEditBoardId}
                placeholder="Select board"
                searchable
              />
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium">Lecturer</p>
              <Combobox
                options={lecturerOptions}
                selectedOption={editLecturerId}
                onSelect={setEditLecturerId}
                placeholder="Select lecturer"
                searchable
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="mb-1.5 text-sm font-medium">Course Code</p>
                <Input
                  value={editCourseCode}
                  onChange={(e) => setEditCourseCode(e.target.value)}
                  placeholder="e.g., CS101"
                />
              </div>
              <div>
                <p className="mb-1.5 text-sm font-medium">Full Marks</p>
                <Input
                  type="number"
                  min="0"
                  value={editCourseFullMarks}
                  onChange={(e) => setEditCourseFullMarks(e.target.value)}
                  placeholder="e.g., 100"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditCourseOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleEditCourseSubmit();
              }}
              disabled={isPatchingCourse}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddInstructorsDialog
        open={isAddInstructorOpen}
        onOpenChange={setIsAddInstructorOpen}
        selectedInstructorIds={selectedInstructorIds}
        selectedInstructorOption={selectedInstructorOption}
        setSelectedInstructorOption={setSelectedInstructorOption}
        availableInstructorOptions={availableInstructorOptions}
        onAddInstructorSelection={handleAddInstructorSelection}
        selectedInstructorUsers={selectedInstructorUsers}
        onRemoveSelectedInstructor={handleRemoveSelectedInstructor}
        onSubmit={() => void handleSubmitInstructors()}
        isSubmitting={isCreatingTeachers}
        onResetSelection={() => {
          setSelectedInstructorIds([]);
          setSelectedInstructorOption('');
        }}
      />

      <RemoveInstructorDialog
        open={teacherMappingToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setTeacherMappingToDelete(null);
        }}
        teacherMappingToDeleteData={teacherMappingToDeleteData}
        onConfirm={() => void handleDeleteInstructor()}
        isDeleting={isDeletingTeacher}
      />

      <Dialog
        open={isVideoModalOpen}
        onOpenChange={(open) => {
          setIsVideoModalOpen(open);
          if (!open) {
            setEditingVideo(null);
            setVideoTitle('');
            setVideoLink('');
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingVideo ? 'Edit video link' : 'Add video link'}
            </DialogTitle>
            <DialogDescription>
              Add a supported media URL for this chapter.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/40 p-3 text-blue-900 dark:text-blue-100">
            <div className="flex items-start gap-3">
              <Info className="h-4 w-4 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Supported media</p>
                <p className="mt-1">
                  YouTube, Facebook, Vimeo, Streamable, SoundCloud, DailyMotion, Twitch, Mux, Wistia.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-sm font-medium">Title</p>
              <Input
                value={videoTitle}
                onChange={(e) => setVideoTitle(e.target.value)}
                placeholder="Enter title"
              />
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium">Video link</p>
              <Input
                value={videoLink}
                onChange={(e) => setVideoLink(e.target.value)}
                placeholder="https://youtube.com/abc"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => void handleSubmitVideo()}
              disabled={
                !videoTitle.trim() ||
                !videoLink.trim() ||
                isPatchingLmsRepo ||
                isPatchingCourse
              }
            >
              {editingVideo ? 'Update' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteCourseOpen}
        onOpenChange={(open) => {
          if (!open) setIsDeleteCourseOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete course?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this course and related content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => void handleDeleteCourse()}
              disabled={isDeletingCourse}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!videoToDelete}
        onOpenChange={(open) => {
          if (!open) setVideoToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete video link?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected video link from this chapter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => void handleDeleteVideo()}
              disabled={isDeletingLmsRepo}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!documentToDelete}
        onOpenChange={(open) => {
          if (!open) setDocumentToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete attachment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected document from this chapter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => void handleDeleteDocument()}
              disabled={isDeletingLmsRepo}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WithLoadingErrorWrapper>
  );
};

export default CourseDetailPage;
